import React, { useEffect, useRef, useState } from "react";
import { MdClose, MdOutlineFileUpload } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import { closeDrawer, openDrawer, openDrawerUpgrade } from "../../../features/aiDrawerStore";
import { getSpecialTab, getUserName, getUserEmail, selectUuid } from "../../../features/userid";
import { selectCid } from "../../../features/companyid";
import Button from "../../ui/Button";
import { IoIosSend, IoMdClose } from "react-icons/io";
import orb from "../../../images/aiSpeaking.png";
import MessageBlocks from "../../ui/MessageBlocks";
import { TYPES_OF_MESSAGES } from "../../../pages/constants/AiAgent";
import { toast } from "react-toastify";
import { useLocation, useNavigate } from "react-router-dom";
import { SpeechButton } from "./SpeechInput";
import { local_socket_api } from "../../../config";
import { AiSparkleIcon } from "../../../constants/OutreachedCandidates";
import { sendUpgradeEmail } from "../../../utils/sendUpgradeEmail";

(function () {
  const OriginalWebSocket = window.WebSocket;
  const WebSocketWrapper = function (...args) {
    console.log("🌐 NEW WEBSOCKET CREATED:", args[0]);
    const socket = new OriginalWebSocket(...args);

    const originalSend = socket.send;
    socket.send = function (data) {
      return originalSend.call(this, data);
    };

    socket.addEventListener("message", function (event) {
      try {
        const parsed = JSON.parse(event.data);
        console.log("📥 Message Type:", parsed.type);
      } catch (e) { }
    });

    return socket;
  };

  // Copy all static properties from original WebSocket
  WebSocketWrapper.CONNECTING = OriginalWebSocket.CONNECTING;
  WebSocketWrapper.OPEN = OriginalWebSocket.OPEN;
  WebSocketWrapper.CLOSING = OriginalWebSocket.CLOSING;
  WebSocketWrapper.CLOSED = OriginalWebSocket.CLOSED;
  WebSocketWrapper.prototype = OriginalWebSocket.prototype;

  window.WebSocket = WebSocketWrapper;
})();

export const GlobalToggle = ({
  children,
  companyId,
  jobId,
  variant = "primary",
  className = "",
  onClick = () => { },
  onFail = () => { }, // 👈 added optional onFail callback
}) => {
  const dispatch = useDispatch();
  const specialTab = useSelector(getSpecialTab);
  const recruiterUuid = useSelector(selectUuid);
  const [loading, setLoading] = useState(false);
  const wsRef = useRef(null);

  const initializeWebSocket = () => {
    setLoading(true);

    // Close existing socket first
    if (window.activeChatSocket) {
      console.warn("⚠️ Closing existing socket");
      window.activeChatSocket.close();
      window.activeChatSocket = null;
    }

    const socketUrl = `${local_socket_api}?job_uuid=${jobId}&company_uuid=${companyId}&recruiter_uuid=${recruiterUuid || ""}`;
    const socket = new WebSocket(socketUrl);
    socket._debugId = Math.random().toString(36).substring(7);
    wsRef.current = socket;

    let hasOpened = false; // 👈 Track if it ever opened

    socket.onopen = () => {
      hasOpened = true;
      setLoading(false);
      dispatch(openDrawer(jobId));
    };

    socket.onclose = (event) => {
      console.log(
        "❌ Socket closed. ID:",
        socket._debugId,
        "Reason:",
        event.reason
      );
      setLoading(false);

      if (!hasOpened) {
        console.error("🚫 WebSocket connection failed before opening.");
        onFail(event); // 👈 trigger callback
      }
    };

    socket.onerror = (error) => {
      console.error("⚠️ Socket error. ID:", socket._debugId, error);
      setLoading(false);
      toast.error("Something went wrong! Please try again.");
      if (!hasOpened) onFail(error);
    };

    return socket;
  };

  return (
    <Button
      loading={loading}
      onClick={() => {
        if (loading) return;

        if (!specialTab) {
          dispatch(openDrawerUpgrade(jobId));
          onClick();
          return;
        }

        const socket = initializeWebSocket();
        window.activeChatSocket = socket;
        console.log("🌐 Socket initialized. ID:", socket._debugId);
        if (!socket.readyState) return;
        onClick();
      }}
      className={className}
      variant={variant}
    >
      {children}
    </Button>
  );
};

export const AiAgent = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const isOpen = useSelector((state) => state.drawer.isDrawerOpen);
  const upgradeMode = useSelector((state) => state.drawer.upgradeMode);
  const userName = useSelector(getUserName);
  const userEmail = useSelector(getUserEmail);
  const companyId = useSelector(selectCid);
  const [messages, setMessages] = useState([]);
  const [attachedFiles, setAttachedFiles] = useState([]);
  const [inputText, setInputText] = useState("");
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [sourcingProgress, setSourcingProgress] = useState(null);
  const [isCancelling, setIsCancelling] = useState(false);
  // sourcingProgress shape: { isActive, isCancelling, currentMessage, candidatesFound, stageIndex, totalStages, elapsedSecs, completed, cancelled, steps[] }
  const fileInputRef = useRef(null);
  const socketRef = useRef(null);
  const messageEndRef = useRef(null);
  const inputRef = useRef(null);
  const lastComponentTextRef = useRef(null);
  const componentDedupeTimerRef = useRef(null);

  // ⬇️ Scroll to bottom when new message or sourcing update arrives
  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, sourcingProgress]);

  useEffect(() => {
    console.log("=== EFFECT START ===");
    console.log("isOpen:", isOpen);

    if (!isOpen || !window.activeChatSocket) {
      console.log("❌ Exiting effect - conditions not met");
      return;
    }

    const socket = window.activeChatSocket;
    socketRef.current = socket;

    console.log("✅ Attaching handlers to socket");

    // Use addEventListener instead of onmessage
    const messageHandler = (event) => {
      console.log("");
      console.log("╔═══════════════════════════════════════╗");
      console.log("║     MESSAGE HANDLER CALLED            ║");
      console.log("╚═══════════════════════════════════════╝");

      setIsWaitingForResponse(false);

      try {
        const data = JSON.parse(event.data);
        console.log("✅ PARSED SUCCESS");
        console.log("Message type:", data.type);

        // Handle sourcing_progress: update in-place, don't add to messages
        if (data.type === TYPES_OF_MESSAGES.SOURCING_PROGRESS) {
          const msg = data.payload.message || "Sourcing in progress...";
          // Reset isCancelling when new progress arrives (new session started)
          setIsCancelling(false);
          setSourcingProgress((prev) => {
            // If previous session was completed or cancelled, start fresh
            const isNewSession = !prev || prev.completed || prev.cancelled;
            const prevSteps = isNewSession ? [] : (prev?.steps || []);
            // Only add as a new step if the message text changed
            const lastStep = prevSteps[prevSteps.length - 1];
            const steps =
              lastStep?.message === msg
                ? prevSteps
                : [...prevSteps, { message: msg, ts: Date.now() }];
            return {
              isActive: true,
              currentMessage: msg,
              candidatesFound: data.payload.candidates_found || 0,
              stageIndex: data.payload.stage_index || 0,
              totalStages: data.payload.total_stages || 0,
              elapsedSecs: data.payload.elapsed_secs || 0,
              estimatedRemainingSecs: data.payload.estimated_remaining_secs ?? null,
              completed: false,
              cancelled: false,
              steps,
            };
          });
          return;
        }

        // Helper: update the latest outreach message's status
        const updateLatestOutreachStatus = (newStatus) => {
          setMessages((prev) => {
            const updated = [...prev];
            for (let i = updated.length - 1; i >= 0; i--) {
              if (updated[i].type === TYPES_OF_MESSAGES.OUTREACH) {
                updated[i] = {
                  ...updated[i],
                  payload: { ...updated[i].payload, status: newStatus },
                };
                break;
              }
            }
            return updated;
          });
        };

        // Handle sourcing_done / sourcing_zero_results: mark progress as completed
        if (
          data.type === TYPES_OF_MESSAGES.SOURCING_DONE ||
          data.type === TYPES_OF_MESSAGES.SOURCING_ZERO_RESULTS
        ) {
          const jobId = data.type === TYPES_OF_MESSAGES.SOURCING_DONE
            ? data.payload?.job_id || null
            : null;
          setSourcingProgress((prev) =>
            prev
              ? { ...prev, isActive: false, completed: true, jobId }
              : {
                  isActive: false,
                  currentMessage: "",
                  candidatesFound: 0,
                  stageIndex: 0,
                  totalStages: 0,
                  elapsedSecs: 0,
                  completed: true,
                  jobId,
                }
          );
          updateLatestOutreachStatus(
            data.type === TYPES_OF_MESSAGES.SOURCING_DONE ? "completed" : "zero_results"
          );
        }

        // Handle sourcing_cancelled: mark progress as cancelled
        if (data.type === TYPES_OF_MESSAGES.SOURCING_CANCELLED) {
          setIsCancelling(false);
          clearTimeout(cancelTimeoutRef.current);
          setSourcingProgress((prev) =>
            prev
              ? {
                  ...prev,
                  isActive: false,
                  completed: false,
                  cancelled: true,
                  candidatesFound: data.payload.candidates_saved ?? prev.candidatesFound,
                }
              : {
                  isActive: false,
                  currentMessage: "",
                  candidatesFound: data.payload.candidates_saved ?? 0,
                  stageIndex: 0,
                  totalStages: 0,
                  elapsedSecs: 0,
                  completed: false,
                  cancelled: true,
                }
          );
          updateLatestOutreachStatus("cancelled");
        }

        // Handle sourcing_error: mark progress as failed
        if (data.type === TYPES_OF_MESSAGES.SOURCING_ERROR) {
          setIsCancelling(false);
          clearTimeout(cancelTimeoutRef.current);
          setSourcingProgress((prev) =>
            prev
              ? { ...prev, isActive: false, completed: false, cancelled: false, error: true }
              : {
                  isActive: false,
                  currentMessage: "",
                  candidatesFound: 0,
                  stageIndex: 0,
                  totalStages: 0,
                  elapsedSecs: 0,
                  completed: false,
                  error: true,
                }
          );
          updateLatestOutreachStatus("error");
        }

        // Deduplication: if this is a "chat" message that duplicates a recent "component" text, skip it
        if (data.type === TYPES_OF_MESSAGES.CHAT && lastComponentTextRef.current) {
          const chatContent = data.payload?.content || "";
          const componentText = lastComponentTextRef.current;
          lastComponentTextRef.current = null;
          clearTimeout(componentDedupeTimerRef.current);
          // Skip if the chat content matches the component's text fallback
          if (chatContent === componentText || componentText.startsWith(chatContent.substring(0, 50))) {
            return;
          }
        }

        // Track component messages for deduplication and ensure role is set
        if (data.type === TYPES_OF_MESSAGES.COMPONENT) {
          if (!data.payload.role) data.payload.role = "assistant";
          if (data.payload.text) {
            lastComponentTextRef.current = data.payload.text;
            clearTimeout(componentDedupeTimerRef.current);
            componentDedupeTimerRef.current = setTimeout(() => {
              lastComponentTextRef.current = null;
            }, 500);
          }
        }

        if (data.payload?.message_history) {
          // Use sourcing_status from connected payload
          const sourcingStatus = data.payload.sourcing_status || "";
          if (sourcingStatus === "in_progress") {
            setSourcingProgress({
              isActive: true,
              currentMessage: "Sourcing in progress...",
              candidatesFound: 0,
              stageIndex: 0,
              totalStages: 0,
              elapsedSecs: 0,
              completed: false,
            });
          } else {
            setSourcingProgress(null);
          }

          setMessages([
            ...data.payload.message_history.map((m) => {
              if (m.message_type === TYPES_OF_MESSAGES.COMPONENT) {
                try {
                  const componentPayload = JSON.parse(m.content);
                  return {
                    type: m.message_type,
                    payload: {
                      ...componentPayload,
                      role: m.role || "assistant",
                    },
                  };
                } catch {
                  return {
                    type: TYPES_OF_MESSAGES.CHAT,
                    payload: { role: m.role, content: m.content },
                  };
                }
              }

              if (m.message_type === TYPES_OF_MESSAGES.FORM) {
                return {
                  type: m.message_type,
                  payload: {
                    role: m.role,
                    content: m.content,
                    fields: JSON.parse(m.content)?.fields || [],
                  },
                };
              }

              if (m.message_type === TYPES_OF_MESSAGES.OUTREACH) {
                return {
                  type: m.message_type,
                  payload: {
                    role: m.role,
                    content: m.content,
                    status: m.status || "completed",
                  },
                };
              }

              return {
                type: m.message_type,
                payload: {
                  role: m.role,
                  content: m.content,
                },
              };
            }),
            data.type === TYPES_OF_MESSAGES.COMPONENT
              ? {
                  type: data.type,
                  payload: {
                    ...data.payload,
                    role: data.payload.role || "assistant",
                  },
                }
              : {
                  type: data.type,
                  payload: {
                    chat_id: data.payload.chat_id,
                    content: data.payload.content,
                    role: data.payload.role,
                    ...(data.payload.job_id && { job_id: data.payload.job_id }),
                  },
                },
          ]);
        } else {
          setMessages((prev) => {
            // Live outreach messages start as in_progress
            const msg = data.type === TYPES_OF_MESSAGES.OUTREACH
              ? { ...data, payload: { ...data.payload, status: "in_progress" } }
              : data;
            return [...prev, msg];
          });
        }
      } catch (err) {
        console.error("╔═══════════════════════════════════════╗");
        console.error("║     PARSE ERROR                       ║");
        console.error("╚═══════════════════════════════════════╝");
        console.error("Error:", err.message);
      }
    };

    const closeHandler = (event) => {
      console.warn(
        "🔴 Socket closed. Code:",
        event.code,
        "Reason:",
        event.reason
      );
      setIsWaitingForResponse(false);
    };
    //test comment

    const errorHandler = (error) => {
      console.error("💥 Socket error:", error);
      toast.error("WebSocket connection error!");
      setIsWaitingForResponse(false);
    };

    // Use addEventListener for better control
    socket.addEventListener("message", messageHandler);
    socket.addEventListener("close", closeHandler);
    socket.addEventListener("error", errorHandler);

    // Verify attachment
    setTimeout(() => {
      console.log("🧪 Verification check:");
      console.log("  Socket still exists:", !!socketRef.current);
      console.log("  Socket readyState:", socketRef.current?.readyState);
      console.log("  Is same socket:", socketRef.current === socket);
    }, 500);

    return () => {
      console.log("🧹 CLEANUP: Removing all listeners");
      socket.removeEventListener("message", messageHandler);
      socket.removeEventListener("close", closeHandler);
      socket.removeEventListener("error", errorHandler);
      clearTimeout(componentDedupeTimerRef.current);
      lastComponentTextRef.current = null;
    };
  }, [isOpen]);

  const handleSend = async () => {
    console.log("");
    console.log("▶▶▶ HANDLE SEND CALLED ◀◀◀");

    if (!inputText.trim() || isWaitingForResponse) {
      console.log("❌ Blocked: Empty text or waiting");
      return;
    }

    let messageToSend = null;

    try {
      if (attachedFiles.length > 0) {
        console.log("📎 File detected, converting to Base64...");
        const file = attachedFiles[0];

        const base64Data = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result.split(",")[1]);
          reader.onerror = (err) => reject(err);
          reader.readAsDataURL(file);
        });

        messageToSend = {
          type: "resume_upload",
          payload: {
            role: "user",
            content: inputText.trim() || "",
            file_data: base64Data,
            filename: file.name,
          },
        };

        setMessages((prev) => [...prev, messageToSend]);

        setAttachedFiles([]);
      } else {
        messageToSend = {
          type: "chat",
          payload: {
            role: "user",
            content: inputText.trim(),
          },
        };

        setMessages((prev) => [...prev, messageToSend]);
      }

      setInputText("");
      setIsWaitingForResponse(true);

      if (socketRef.current?.readyState === WebSocket.OPEN) {
        console.log("📤 Sending message via socket");
        socketRef.current.send(JSON.stringify(messageToSend));
        console.log("✅ Message sent");
      } else {
        console.warn(
          "⚠️ WebSocket not open. ReadyState:",
          socketRef.current?.readyState
        );
        setIsWaitingForResponse(false);
      }
    } catch (err) {
      console.error("💥 Error sending message:", err);
      toast.error("Failed to process your message or file");
      setIsWaitingForResponse(false);
    } finally {
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  };

  useEffect(() => {
    if (!isWaitingForResponse) {
      inputRef.current?.focus();
    }
  }, [isWaitingForResponse]);

  const cancelTimeoutRef = useRef(null);

  const handleCancelSourcing = () => {
    if (isCancelling) return;
    setIsCancelling(true);

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(
        JSON.stringify({
          type: "cancel_sourcing",
          timestamp: new Date().toISOString(),
          payload: {},
        })
      );
    }

    // Fallback: if backend doesn't respond within 15s, reset cancelling state
    clearTimeout(cancelTimeoutRef.current);
    cancelTimeoutRef.current = setTimeout(() => {
      setIsCancelling((current) => {
        if (current) {
          toast.error("Cancel request timed out. You may close the drawer.");
        }
        return false;
      });
    }, 15000);
  };

  const handleClose = () => {
    dispatch(closeDrawer());
    setSourcingProgress(null);
    setIsCancelling(false);
    if (socketRef.current) {
      socketRef.current.close();
      socketRef.current = null;
      window.activeChatSocket = null;
    }
    if (!location.pathname.includes("/outreach")) navigate("/jobs");
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const pdfFiles = files.filter((f) => f.type === "application/pdf");
    setAttachedFiles((prev) => [...prev, ...pdfFiles].slice(0, 1));
    e.target.value = null;
  };

  const handleTranscriptComplete = (transcript) => {
    setInputText(transcript);
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  };

  const removeFile = (name) =>
    setAttachedFiles((prev) => prev.filter((f) => f.name !== name));

  const isInputDisabled = isWaitingForResponse;

  const backgroundOrbStyle = {
    position: "absolute",
    top: 0,
    right: "50%",
    width: "350px",
    height: "150px",
    transform: "translate(50%, -50%)",
    zIndex: 0,
    opacity: 0.5,
  };

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-50 backdrop-blur-sm"
          onClick={handleClose}
        ></div>
      )}

      <div
        className={`fixed top-0 -right-6 h-full z-50 w-full sm:w-[420px] lg:w-[600px]
        transform transition-transform duration-500 ease-in-out
        shadow-2xl backdrop-blur-xl bg-transparent py-1 mx-2
        ${isOpen ? "translate-x-0 right-0" : "translate-x-full"}`}
      >
        <div className="bg-gradient-to-b from-[#E4F2FF] to-white h-full rounded-lg">
          <div className="p-4 flex justify-between items-center absolute top-0 left-0 w-full z-[400]">
            <Button onClick={handleClose} variant="secondary" size="iconSm">
              <IoMdClose className="text-sm" />
            </Button>
          </div>

          {upgradeMode ? (
            <div className="flex flex-col items-center justify-center h-full px-8 text-center relative z-10">
              <img
                src={orb}
                className="absolute"
                alt="Orb"
                style={backgroundOrbStyle}
              />
              <AiSparkleIcon width={48} height={48} />
              <h2 className="text-2xl font-semibold text-primaryBlack mt-6">
                Still Doing Traditional Search?
              </h2>
              <p className="text-sm text-primaryBlack/60 mt-3 max-w-xs leading-relaxed">
                Find qualified candidates with Northstarz assisted sourcing.
              </p>
              <button
                onClick={async () => {
                  if (sendingEmail || emailSent) return;
                  setSendingEmail(true);
                  try {
                    await sendUpgradeEmail({ userName, companyId, userEmail });
                    setEmailSent(true);
                    toast.success("Email sent! Our team will reach out to you shortly.");
                  } catch (err) {
                    toast.error("Failed to send email. Please try again.");
                  } finally {
                    setSendingEmail(false);
                  }
                }}
                disabled={sendingEmail || emailSent}
                className="mt-8 inline-block text-white font-medium text-sm px-6 py-3 rounded-lg bg-gradient-to-r from-[#148EFF] to-[#FF8BE4] hover:shadow-lg transition-shadow cursor-pointer disabled:opacity-60"
              >
                {emailSent ? "Request Submitted \u2014 Our Team Will Reach Out Shortly" : sendingEmail ? "Sending..." : "Unlock Sia"}
              </button>
            </div>
          ) : (
            <>
              <div className="flex flex-col h-full relative z-10 overflow-hidden">
                <img
                  src={orb}
                  className="absolute"
                  alt="Orb"
                  style={backgroundOrbStyle}
                />

                {/* Messages */}
                <div className="flex-grow p-4 overflow-y-auto">
                  <div className="flex flex-col gap-6 justify-center relative z-1 min-h-full h-max">
                    {messages.map((msg, i) => (
                      <MessageBlocks
                        key={i}
                        isAI={msg.payload.role === "assistant"}
                        body={msg}
                        type={msg.type}
                        showFormButton={
                          msg.type === TYPES_OF_MESSAGES.FORM &&
                          (i === messages.length - 1 || i === messages.length - 2)
                        }
                        isLast={messages.length - 1 === i}
                        sourcingProgress={
                          msg.type === TYPES_OF_MESSAGES.OUTREACH &&
                          msg.payload.status === "in_progress"
                            ? sourcingProgress
                            : null
                        }
                        isCancelling={isCancelling}
                        onCancelSourcing={handleCancelSourcing}
                      />
                    ))}

                    {isWaitingForResponse && (
                      <div className="flex justify-start">
                        <div className="bg-gray-100 text-gray-600 px-3 py-2 rounded-2xl inline-block">
                          Thinking
                          <span className="typing-dots">
                            <span>.</span>
                            <span>.</span>
                            <span>.</span>
                          </span>
                        </div>
                      </div>
                    )}

                    <div ref={messageEndRef} />
                  </div>
                </div>

                <div className="mb-2 px-2">
                  {attachedFiles.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-2">
                      {attachedFiles.map((f, i) => (
                        <div
                          key={i}
                          className="flex items-center bg-indigo-100 text-indigo-700 text-xs font-semibold px-3 py-1 rounded-full"
                        >
                          {f.name}
                          <Button
                            onClick={() => removeFile(f.name)}
                            variant="icon"
                            size="icon"
                          >
                            <MdClose size={12} />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}

                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    accept=".pdf"
                    multiple
                    className="hidden"
                  />

                  <div className="relative flex items-center border border-[#BFCFFF] rounded-lg shadow-lg shadow-[#638BFF33] overflow-hidden bg-white p-1 mt-2">
                    <input
                      type="text"
                      placeholder={
                        isWaitingForResponse
                          ? "Waiting for AI response..."
                          : "Ask a follow-up"
                      }
                      className="w-full px-3 py-3 text-gray-700 focus:outline-none bg-transparent"
                      value={inputText}
                      onChange={(e) => setInputText(e.target.value)}
                      disabled={isWaitingForResponse}
                      ref={inputRef}
                      onKeyDownCapture={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleSend();
                        }
                      }}
                    />

                    <Button
                      onClick={() =>
                        !isWaitingForResponse &&
                        attachedFiles.length < 1 &&
                        fileInputRef.current.click()
                      }
                      disabled={isWaitingForResponse || attachedFiles.length >= 1}
                      variant="secondary"
                      size="icon"
                      className="!p-2 h-fit rounded-full mr-1"
                    >
                      <MdOutlineFileUpload size={15} />
                    </Button>

                    <Button
                      onClick={handleSend}
                      disabled={isInputDisabled}
                      variant="black"
                      size="icon"
                      className="py-2 px-3 h-fit rounded-3xl mr-1 bg-[#0F172A] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <IoIosSend size={16} />
                    </Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        .typing-dots span {
          animation: blink 1.4s infinite both;
          font-weight: bold;
        }
        .typing-dots span:nth-child(2) { animation-delay: 0.2s; }
        .typing-dots span:nth-child(3) { animation-delay: 0.4s; }
        @keyframes blink {
          0%, 80%, 100% { opacity: 0; }
          40% { opacity: 1; }
        }
      `}</style>
    </>
  );
};

export const useHandleMessageGlobally = (inputText) => {
  const socketRef = useRef(null);
  const isOpen = useSelector((state) => state.drawer.isDrawerOpen);

  useEffect(() => {
    if (isOpen && window.activeChatSocket) {
      socketRef.current = window.activeChatSocket;

      return () => {
        if (socketRef.current) socketRef.current.onmessage = null;
      };
    }
  }, [isOpen]);

  const newUserMessage = {
    type: "form_submit",
    payload: {
      form_type: "job_requirements",
      data: {
        fields: inputText,
      },
    },
  };

  const sendMessage = () => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(newUserMessage));
    } else {
      console.warn("⚠️ WebSocket not open; cannot send message.");
    }
  };

  return { sendMessage };
};

export const useHandleCloseSocket = () => {
  const dispatch = useDispatch();

  const handleClose = () => {
    dispatch(closeDrawer());
    if (window.activeChatSocket) {
      window.activeChatSocket.close();
      window.activeChatSocket = null;
    }
  };

  return { handleClose };
};
